import React, { useCallback, useEffect, useRef, useState } from 'react';
// import './style/Popover.css'; TODO: Fix webpack/babel issue and remove .scss file
export const Popover = ({
  children,
  id,
  panelAlignment = 'left',
  buttonAriaLabel,
  buttonContent,
  closeOnSelection = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const wrapperRef = useRef(null);

  const closeOnEscape = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        closePanel();
      }
    },
    [isOpen]
  );

  const closePanel = useCallback(
    (focusButton = true) => {
      const panel = panelRef.current;
      if (focusButton) {
        buttonRef.current?.focus();
      }
      if (panel) {
        panel.classList.add('hidden');
      }
      setTimeout(() => {
        setIsOpen(false);
      }, 350);
    },
    [setIsOpen, panelRef]
  );

  const handleDocumentClick = useCallback(
    (event) => {
      if (wrapperRef?.current && !wrapperRef.current.contains(event.target)) {
        closePanel(false);
      }
      if (closeOnSelection && panelRef?.current && panelRef.current.contains(event.target)) {
        closePanel(false);
      }
    },
    [closePanel]
  );

  const handleTriggerButtonClick = () => {
    isOpen ? closePanel() : setIsOpen(true);
  };

  const lockFocusToModal = (containerElement, closeModal) => {
    if (!containerElement) return;
    const focusableElements = containerElement?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements) return;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (firstElement) {
      firstElement.focus();
    }
    const handleTabKeyPress = (event) => {
      if (event.key === 'Tab') {
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };
    const handleEscapeKeyPress = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    const handleArrowKeyPress = (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (document.activeElement === lastElement) {
          firstElement.focus();
        } else {
          const index = Array.from(focusableElements).indexOf(
            document.activeElement
          );
          focusableElements[index + 1].focus();
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (document.activeElement === firstElement) {
          lastElement.focus();
        } else {
          const index = Array.from(focusableElements).indexOf(
            document.activeElement
          );
          focusableElements[index - 1].focus();
        }
      }
    };
    containerElement?.addEventListener('keydown', handleTabKeyPress);
    containerElement?.addEventListener('keydown', handleEscapeKeyPress);
    containerElement?.addEventListener('keydown', handleArrowKeyPress);
    return () => {
      containerElement?.removeEventListener('keydown', handleTabKeyPress);
      containerElement?.removeEventListener('keydown', handleEscapeKeyPress);
      containerElement?.removeEventListener('keydown', handleArrowKeyPress);
    };
  };

  useEffect(() => {
    const panel = panelRef.current;
    if (isOpen) {
      document.addEventListener('click', handleDocumentClick);
      document.addEventListener('keydown', closeOnEscape);
      if (panel) {
        lockFocusToModal(panel, closePanel);
        panel.classList.remove('hidden');
      }
    } else {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', closeOnEscape);
      if (panel) {
        panel.classList.add('hidden');
      }
    }
    document.body.classList.toggle('account-popover-opened', isOpen);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className={`popover__wrapper popover ${id}`}>
      <PopoverButton
        id={id}
        isOpen={isOpen}
        buttonRef={buttonRef}
        handleTriggerButtonClick={handleTriggerButtonClick}
        buttonAriaLabel={buttonAriaLabel}
      >
        {buttonContent}
      </PopoverButton>
      <PopoverPanel
        id={id}
        isOpen={isOpen}
        panelRef={panelRef}
        panelAlignment={panelAlignment}
      >
        {children}
      </PopoverPanel>
    </div>
  );
};

const PopoverButton = ({
  children,
  id,
  isOpen,
  buttonRef,
  handleTriggerButtonClick,
  buttonAriaLabel,
}) => {
  return (
    <button
      ref={buttonRef}
      className="popover__button"
      aria-label={buttonAriaLabel}
      aria-expanded={isOpen}
      aria-controls={id}
      onClick={() => handleTriggerButtonClick()}
    >
      {children}
    </button>
  );
};

PopoverButton.displayName = 'PopoverButton';
Popover.Button = PopoverButton;

const PopoverPanel = ({ children, id, isOpen, panelRef, panelAlignment }) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className="popover__panel hidden"
      aria-hidden={!isOpen}
      id={id}
      style={{
        [panelAlignment === 'left' ? 'left' : 'right']: 0,
      }}
    >
      {children}
    </div>
  );
};

PopoverPanel.displayName = 'PopoverPanel';
Popover.Panel = PopoverPanel;

export default Popover;
